import { useMemo } from 'react';
import type { CoreEnv } from '@eventbrite/legacy-infra.context-types';
import { useIntl } from 'react-intl';
import { Tags } from '@eventbrite/legacy-infra.tags';
import type { TagObject } from '@eventbrite/legacy-infra.tags/types';
import type { City, TLD } from '@/shared/types';
import styles from './PopularCitiesContainer.module.scss';
import { getPopularCitiesForLocale } from './getStaticPopularCities';

interface PopularCitiesContainerProps {
    languageSubdirectory?: string;
    tld: string;
    localeInfo: CoreEnv['localeInfo'];
    /**
     * Extra location slugs to suppress from the pills (in addition to the static
     * top destinations), so a city shown as a destination card isn't repeated
     * here. Used when the expanded US /d/ city cards are enabled.
     */
    additionalExcludedSlugs?: string[];
}

const createTagObject = (
    formatMessage: ReturnType<typeof useIntl>['formatMessage'],
    languageSubdirectory?: string,
    popularCities: City[] = [],
): TagObject[] =>
    popularCities.map((city) => {
        const url = `${languageSubdirectory ? '/' + languageSubdirectory : ''}/ttd/${city.location_slug}/`;
        const cityName =
            typeof city.display_name === 'string'
                ? city.display_name
                : formatMessage(city.display_name);

        return {
            displayText: formatMessage(
                {
                    id: 'Things to do in {city}',
                    defaultMessage: 'Things to do in {city}',
                },
                { city: cityName },
            ),
            linkUrl: url,
        };
    });

export const PopularCitiesContainer: React.FunctionComponent<PopularCitiesContainerProps> = ({
    languageSubdirectory,
    tld,
    localeInfo,
    additionalExcludedSlugs,
}) => {
    const { locale } = localeInfo;
    const intl = useIntl();

    const { tags } = useMemo(() => {
        const popularCities = getPopularCitiesForLocale(
            tld as TLD,
            locale,
            additionalExcludedSlugs,
        );
        const tags = createTagObject(intl.formatMessage, languageSubdirectory, popularCities);
        return { popularCities, tags };
    }, [tld, locale, languageSubdirectory, additionalExcludedSlugs, intl.formatMessage]);

    if (!tags || !tags.length) {
        return null;
    }

    return (
        <div className={styles.root} data-testid="popular-cities-container">
            <h2 className={styles.header}>
                {intl.formatMessage({
                    id: 'Popular cities',
                    defaultMessage: 'Popular cities',
                })}
            </h2>
            <Tags tags={tags} />
        </div>
    );
};
